import type { SideNavV2Item, SideNavV2Section } from "@/components/interactive/SideNavV2";
import { getProjectNavCategorySidebarSections } from "@/components/navigation/project-category-nav";
import {
  useCanArrangeCategoryNav,
  useShowsArrangedCategoryNav,
} from "@/utils/hooks/use-category-nav-enabled";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useCallback, useMemo } from "react";

interface ProjectNavRailPinStore extends Record<string, unknown> {
  itemIds: string[];
}

export const EMPTY_RAIL_PIN_STORE: ProjectNavRailPinStore = { itemIds: [] };

/** Appends a pin, keeping pin order; pinning twice is a no-op. */
export const addRailPin = (itemIds: readonly string[], itemId: string): string[] =>
  itemIds.includes(itemId) ? [...itemIds] : [...itemIds, itemId];

export const removeRailPin = (itemIds: readonly string[], itemId: string): string[] =>
  itemIds.filter((id) => id !== itemId);

/** `itemIds` with the entry at `from` moved to `to`, the entries between shifting to make room. */
export const moveRailItem = (itemIds: readonly string[], from: number, to: number): string[] => {
  const next = [...itemIds];
  const [moved] = next.splice(from, 1);
  if (moved == null) return [...itemIds];
  next.splice(to, 0, moved);
  return next;
};

/**
 * A stored rail order once the reader has reordered the items they can see (`visibleOrder`, a
 * reordering of the `itemIds` entries it names). Items they cannot see, such as a pin whose row a
 * flag now hides, keep their places; the visible items take the visible items' places in their
 * new order.
 */
export const reorderVisibleRailItems = (
  itemIds: readonly string[],
  visibleOrder: readonly string[],
): string[] => {
  const visible = new Set(visibleOrder);
  const remaining = [...visibleOrder];
  return itemIds.map((id) => (visible.has(id) ? remaining.shift() ?? id : id));
};

/**
 * The rows the category sidebars show that can be pinned: every row with a destination, keyed by
 * id. Group titles and disabled placeholders are not rows you can go to, so they are left out.
 */
export const collectPinnableRailItems = <T extends string>(
  categories: readonly SideNavV2Section<T>[],
): ReadonlyMap<string, SideNavV2Item<T>> =>
  new Map(
    categories
      .flatMap((category) => getProjectNavCategorySidebarSections(category))
      .flatMap((section) => section.items)
      .filter((item) => item.href != null && !item.disabled)
      .map((item) => [item.id, item] as const),
  );

/**
 * Pinned ids as the rows they name today, in pin order. Pins store only ids, so a renamed row
 * shows its new label, and a row the reader can no longer see (a flag turned off) drops out.
 */
export const resolveRailPins = <T extends string>(
  itemIds: readonly string[],
  pinnable: ReadonlyMap<string, SideNavV2Item<T>>,
): SideNavV2Item<T>[] =>
  itemIds.flatMap((id) => {
    const item = pinnable.get(id);
    return item == null ? [] : [item];
  });

/**
 * Sidebar items pinned to the rail, with commands to pin and unpin. A reader whose nav shows the
 * default arrangement (`useShowsArrangedCategoryNav`) has no pins, and one who may not arrange it
 * (`useCanArrangeCategoryNav`) stores none.
 */
export const useProjectRailPins = () => {
  const showsArranged = useShowsArrangedCategoryNav();
  const canArrange = useCanArrangeCategoryNav();
  const [storedPins, setStoredPins] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_RAIL_PINS,
    EMPTY_RAIL_PIN_STORE,
  );
  const store = showsArranged ? storedPins : EMPTY_RAIL_PIN_STORE;
  const setStore: typeof setStoredPins = useCallback(
    (update) => {
      if (canArrange) setStoredPins(update);
    },
    [canArrange, setStoredPins],
  );
  const pinnedItemIds = useMemo(
    () => (Array.isArray(store.itemIds) ? store.itemIds : []),
    [store.itemIds],
  );
  const pinToRail = useCallback(
    (itemId: string) =>
      setStore((current) => ({ itemIds: addRailPin(current?.itemIds ?? [], itemId) })),
    [setStore],
  );
  const unpinFromRail = useCallback(
    (itemId: string) =>
      setStore((current) => ({ itemIds: removeRailPin(current?.itemIds ?? [], itemId) })),
    [setStore],
  );
  /** Stores a new order for the pins the reader can see; see `reorderVisibleRailItems`. */
  const setVisibleRailPinOrder = useCallback(
    (visibleOrder: readonly string[]) =>
      setStore((current) => ({
        itemIds: reorderVisibleRailItems(current?.itemIds ?? [], visibleOrder),
      })),
    [setStore],
  );

  return { pinnedItemIds, pinToRail, unpinFromRail, setVisibleRailPinOrder };
};

interface RailCategoryOrderStore extends Record<string, unknown> {
  itemIds: string[];
}

export const EMPTY_RAIL_CATEGORY_ORDER: RailCategoryOrderStore = { itemIds: [] };

/**
 * `entries` (rail categories, sidebar sections) in the reader's stored order. The entries it
 * names take their own places in that order; any it does not name, such as one added since, keep
 * their default places.
 */
export const applyStoredOrder = <T extends { id: string }>(
  entries: readonly T[],
  storedOrder: readonly string[],
): T[] => {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const ids = reorderVisibleRailItems(
    entries.map((entry) => entry.id),
    storedOrder.filter((id) => byId.has(id)),
  );
  return ids.flatMap((id) => {
    const entry = byId.get(id);
    return entry == null ? [] : [entry];
  });
};

/**
 * A stored order once the reader has reordered the entries they can see. Stored entries out of
 * view, such as Admin outside admin mode, keep their places; entries new to the store join at the
 * end.
 */
export const mergeVisibleOrder = (
  storedOrder: readonly string[],
  visibleOrder: readonly string[],
): string[] =>
  reorderVisibleRailItems(
    [...storedOrder, ...visibleOrder.filter((id) => !storedOrder.includes(id))],
    visibleOrder,
  );

/**
 * The reader's order for the rail's category icons, and a command to store a new one. A reader
 * whose nav shows the default arrangement (`useShowsArrangedCategoryNav`) sees the default order,
 * and one who may not arrange it (`useCanArrangeCategoryNav`) stores none.
 */
export const useRailCategoryOrder = () => {
  const showsArranged = useShowsArrangedCategoryNav();
  const canArrange = useCanArrangeCategoryNav();
  const [storedOrder, setStoredOrder] = useLocalStorageSyncedState(
    LocalStorageKey.PROJECT_NAV_RAIL_CATEGORY_ORDER,
    EMPTY_RAIL_CATEGORY_ORDER,
  );
  const store = showsArranged ? storedOrder : EMPTY_RAIL_CATEGORY_ORDER;
  const setStore: typeof setStoredOrder = useCallback(
    (update) => {
      if (canArrange) setStoredOrder(update);
    },
    [canArrange, setStoredOrder],
  );
  const categoryOrder = useMemo(
    () => (Array.isArray(store.itemIds) ? store.itemIds : []),
    [store.itemIds],
  );
  /** Stores the order of the categories the reader can see; see `mergeVisibleOrder`. */
  const setVisibleCategoryOrder = useCallback(
    (visibleOrder: readonly string[]) =>
      setStore((current) => ({
        itemIds: mergeVisibleOrder(current?.itemIds ?? [], visibleOrder),
      })),
    [setStore],
  );

  return { categoryOrder, setVisibleCategoryOrder };
};
